import React, { useState, useEffect } from "react";
import EventGrid from "./components/EventGrid/EventGrid";
import { fetchEvents, getUpcomingEvents, getPastEvents } from "../../services/eventService";
import SEO from '../../components/SEO/SEO';
import { seoConfig } from '../../config/seoConfig';
import StarLinkSection from './components/StarLinkSection/StarLinkSection';
import TRCSLSection from './components/TRCSLSection/TRCSLSection';
import EventTabSelector from './components/EventTabSelector/EventTabSelector';
import EventCardSkeleton from './components/EventCardSkeleton/EventCardSkeleton';

const Events = () => {
  const [allEvents, setAllEvents] = useState({});
  const [loading, setLoading] = useState(true);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [activeTab, setActiveTab] = useState(null);
  const [eventCategories, setEventCategories] = useState([]);
  
  useEffect(() => {
    const loadEvents = async () => {
      const data = await fetchEvents();
      setAllEvents(data);
      setLoading(false);
      if (data) {
        // Merge upcoming and past events by category
        const upcomingEvents = getUpcomingEvents(data);
        const pastEvents = getPastEvents(data);
        
        // Combine both upcoming and past events for each category
        const combined = {};
        
        // Add all categories from upcoming events
        Object.entries(upcomingEvents).forEach(([eventType, eventData]) => {
          combined[eventType] = {
            title: eventData.title,
            events: [...eventData.events]
          };
        });
        
        // Add past events to existing categories or create new ones
        Object.entries(pastEvents).forEach(([eventType, eventData]) => {
          if (combined[eventType]) {
            // Add past events after upcoming events (they will naturally be at the end)
            combined[eventType].events.push(...eventData.events);
          } else {
            // Create new category for past events only
            combined[eventType] = {
              title: eventData.title.replace('Past ', ''),
              events: [...eventData.events]
            };
          }
        });
        
        // Sort events within each category: Open (upcoming with registerLink) → Open Soon (upcoming without registerLink) → Past
        Object.keys(combined).forEach(eventType => {
          combined[eventType].events.sort((a, b) => {
            const aIsPast = a.status === 'completed' || a.status === 'past';
            const bIsPast = b.status === 'completed' || b.status === 'past';
            const aIsOpen = !aIsPast && a.registerLink;
            const bIsOpen = !bIsPast && b.registerLink;
            const aIsOpenSoon = !aIsPast && !a.registerLink;
            const bIsOpenSoon = !bIsPast && !b.registerLink;
            
            // Priority order: Open → Open Soon → Past
            if (aIsOpen && !bIsOpen) return -1;
            if (!aIsOpen && bIsOpen) return 1;
            if (aIsOpenSoon && bIsPast) return -1;
            if (aIsPast && bIsOpenSoon) return 1;
            
            // If both are same priority, sort by ID in descending order
            return b.id - a.id;
          });
        });
        
        setAllEvents(combined);
        
        // Extract categories dynamically from the data
        const categories = Object.entries(combined).map(([key, value]) => ({
          key: key,
          title: value.title
        }));
        
        setEventCategories(categories);
        
        // Set the first category as active by default
        if (categories.length > 0) {
          setActiveTab(categories[0].key);
        }
      }
    };
    loadEvents();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (loading) {
        setShowComingSoon(true);
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, [loading]);
  
  // Get filtered events by status
  // const upcomingEvents = getUpcomingEvents();
  // const pastEvents = getPastEvents();

  return (
    <>
      <SEO 
        title={seoConfig.events.title}
        description={seoConfig.events.description}
        keywords={seoConfig.events.keywords}
        ogImage={seoConfig.events.ogImage}
        canonical={seoConfig.events.canonical}
      />
      <div
        data-layer="Events-Desktop"
        className="EventsDesktop w-full min-h-screen bg-Color-White inline-flex flex-col justify-start items-center gap-2 overflow-hidden"
      >
      <div         
        data-layer="Event Header"         
        className="EventHeader w-full max-w-[1920px] px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 py-6 sm:py-8 md:py-10 inline-flex flex-col lg:flex-row justify-center items-center"       
      >         
        <div           
          data-layer="Contact Info Header"           
          className="ContactInfoHeader flex-1 inline-flex flex-col justify-start items-start gap-4 sm:gap-6 md:gap-8 py-3 sm:py-4 md:py-5"         
        >           
          <div             
            data-layer="Celebrating a Legacy"             
            className="CelebratingALegacy self-stretch justify-start"           
          >             
            <span className="text-Color-Black text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal font-['Montserrat']">               
              Events: Celebrating{" "}             
            </span>             
            <span className="text-Color-Brand text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-semibold font-['Montserrat'] underline">               
              25 Years!             
            </span>           
          </div>         
        </div>         
        <div           
          data-layer="Contact Info Header"           
          className="ContactInfoHeader flex-1 max-w-full lg:max-w-[720px] pl-0 lg:pl-16 pt-4 lg:pt-0 py-4 sm:py-5 md:py-6 inline-flex flex-col justify-start items-start gap-4 sm:gap-6 md:gap-8"         
        >           
          <div             
            data-layer="Contact Info Description"             
            className="ContactInfoDescription self-stretch justify-start text-Color-Black text-base sm:text-lg font-medium font-['Montserrat'] tracking-tight"           
          >             
            The Faculty of Engineering, University of Ruhuna, is thrilled to             
            celebrate its 25th Anniversary! We're diligently planning a series             
            of engaging and memorable events to mark this significant milestone.           
          </div>           
          {/* <div             
            data-layer="Contact Info Description"             
            className="ContactInfoDescription self-stretch justify-start text-Color-Black text-sm sm:text-base md:text-lg font-bold font-['Montserrat'] uppercase tracking-tight"           
          >             
            <span className="bg-yellow-100 py-1 px-2 rounded inline-block">               
              Exciting announcements about our anniversary celebrations are               
              coming soon!             
            </span>           
          </div>          */}
        </div>       
      </div>

      {/* All Events Section */}
      <div className="w-full">
        {loading && !showComingSoon ? (
          <div className="w-full max-w-[1920px] px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 py-8">
            {/* Skeleton Loader Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
              {[...Array(6)].map((_, index) => (
                <EventCardSkeleton key={index} />
              ))}
            </div>
          </div>
        ) : showComingSoon || (Object.keys(allEvents).length === 0 || 
         Object.values(allEvents).every(eventData => !eventData.events || eventData.events.length === 0)) ? (
          <div className="w-full max-w-[1920px] px-4 sm:px-8 md:px-12 lg:px-16 xl:px-24 py-16">
            <div className="flex flex-col items-center justify-center py-20 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-xl">
              <div className="text-8xl mb-6">🚀</div>
              <h3 className="text-3xl font-bold text-gray-800 mb-4 font-['Montserrat']">
                Exciting Events Coming Soon!
              </h3>
              <p className="text-gray-600 text-center max-w-2xl text-lg font-['Montserrat'] leading-relaxed">
                We're working hard to bring you amazing events as part of our 25th Anniversary celebration. 
                Stay connected with us for the latest updates on webinars, hackathons, workshops, and special anniversary events!
              </p>
              <div className="mt-8 flex gap-4">
                <div className="bg-white px-6 py-3 rounded-full shadow-md">
                  <span className="text-Color-Brand font-semibold font-['Montserrat']">📧 Email Updates</span>
                </div>
                <div className="bg-white px-6 py-3 rounded-full shadow-md">
                  <span className="text-Color-Brand font-semibold font-['Montserrat']">🔔 Push Notifications</span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          /* Tab Selector and Filtered Event Grid */
          <>
            <EventTabSelector 
              categories={eventCategories}
              activeTab={activeTab}
              onTabChange={setActiveTab}
            />
            
            {activeTab && allEvents[activeTab] && (
              <EventGrid 
                title={allEvents[activeTab].title}
                events={allEvents[activeTab].events}
              />
            )}
          </>
        )}
      </div>

      {/* Star Link Section */}
      <StarLinkSection />

      {/* TRCSL Section */}
      <TRCSLSection />
      </div>
    </>
  );
};

export default Events;
